import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

// Every filled button in the app resolves its fill from --brand-button and its LABEL
// from --brand-on-button, which branding.ts recomputes per palette. That pairing is
// the whole point: a light Button color (a pale mint, say) silently produced white
// text on white before, and CSS has no portable way to pick readable ink.

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-brand-button text-brand-on-button hover:opacity-90",
  secondary:
    "border border-brand-border bg-brand-surface text-brand-text-primary hover:border-brand-primary hover:bg-brand-subtle",
  ghost: "text-brand-text-secondary hover:bg-brand-subtle hover:text-brand-text-primary",
  danger: "border border-n-critical/40 text-n-critical hover:bg-n-critical/10",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-[13px]",
  md: "h-10 px-4 text-sm",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

export function Button({
  variant = "secondary",
  size = "md",
  type = "button",
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        "inline-flex shrink-0 items-center justify-center gap-2 rounded-[var(--radius-ctl)] font-medium",
        "transition-[background-color,border-color,opacity] duration-150 ease-out",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
        "disabled:cursor-not-allowed disabled:opacity-40",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
